import { useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ConfirmDialog } from "primereact/confirmdialog";

import InterviewQuestionUpdate from "./InterviewQuestionUpdate";
import {
  IInterviewQuestion,
  IInterviewQuestionListProps,
  DIFFICULTY_LEVELS,
} from "./InterviewQuestion.model";
import { interviewQuestionService } from "../../service/InterviewQuestion/interviewQuestion.service";
import { Can } from "../../ability/can";
import {
  PERMISSIONS_ACTION,
  PERMISSIONS_MODEL,
} from "../../library/utilities/constants/permissions";
import { Tag } from "primereact/tag";

const InterviewQuestionList = ({
  openNew,
  loadInterviewQuestions,
  data,
  loading,
}: IInterviewQuestionListProps) => {
  const [questionDialog, setQuestionDialog] = useState(false);
  const [selectedQuestion, setSelectedQuestion] =
    useState<IInterviewQuestion | null>(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const deleteQuestion = (id: number) => {
    setDeleteId(id);
    setConfirmDialog(true);
  };

  const confirmDeleteQuestion = async () => {
    if (!deleteId) return;
    await interviewQuestionService.remove(deleteId);

    setConfirmDialog(false);
    loadInterviewQuestions();
  };

  const actionBodyTemplate = (row: IInterviewQuestion) => {
    return (
      <div className="flex gap-2">
        <Can
          I={PERMISSIONS_ACTION.INTERVIEW_QUESTION_CHANGE}
          a={PERMISSIONS_MODEL.INTERVIEW_QUESTION}>
          <Button
            label="Edit"
            icon="pi pi-pencil"
            onClick={() => {
              setSelectedQuestion(row);
              setQuestionDialog(true);
            }}
          />
        </Can>

        <Can
          I={PERMISSIONS_ACTION.INTERVIEW_QUESTION_DELETE}
          a={PERMISSIONS_MODEL.INTERVIEW_QUESTION}>
          <Button
            label="Delete"
            icon="pi pi-trash"
            severity="danger"
            onClick={() => deleteQuestion(row.id)}
          />
        </Can>
      </div>
    );
  };

  const difficultyBodyTemplate = (row: IInterviewQuestion) => {
    const difficulty = DIFFICULTY_LEVELS.find(
      (d) => d.value === row.difficulty_level,
    );
    const severity =
      row.difficulty_level === 1
        ? "success"
        : row.difficulty_level === 2
          ? "warning"
          : "danger";
    return <Tag value={difficulty?.label} severity={severity} />;
  };

  const statusBodyTemplate = (row: IInterviewQuestion) => {
    return row.is_active ? (
      <Tag value="Active" severity="success" />
    ) : (
      <Tag value="Inactive" severity="danger" />
    );
  };

  const choicesBodyTemplate = (row: IInterviewQuestion) => {
    return (
      <div>
        {row.choices.map((choice, idx) => (
          <div
            key={idx}
            className={choice.is_correct ? "font-bold text-green-600" : ""}>
            {idx + 1}. {choice.choice_text}
            {choice.is_correct && " ✓"}
          </div>
        ))}
      </div>
    );
  };

  const questionColumns = [
    {
      field: "question_text",
      header: "Question",
      style: { width: "25%" },
    },
    {
      field: "topic_name",
      header: "Topic",
      style: { width: "10%" },
    },
    {
      field: "difficulty_level",
      header: "Difficulty",
      body: difficultyBodyTemplate,
      style: { width: "10%" },
    },
    {
      field: "choices",
      header: "Choices",
      body: choicesBodyTemplate,
      style: { width: "30%" },
    },
    {
      field: "is_active",
      header: "Status",
      body: statusBodyTemplate,
      style: { width: "10%" },
    },
  ];

  const [globalFilter, setGlobalFilter] = useState("");
  const filteredData = data.filter((question: IInterviewQuestion) => {
    const search = globalFilter.toLowerCase();
    return (
      question.question_text.toLowerCase().includes(search) ||
      question?.topic_name?.toLowerCase().includes(search)
    );
  });

  return (
    <>
      <Dialog
        visible={questionDialog}
        header="Update Interview Question"
        modal
        onHide={() => setQuestionDialog(false)}
        style={{ width: "70vw" }}>
        {selectedQuestion && (
          <InterviewQuestionUpdate
            data={selectedQuestion}
            setQuestionDialog={setQuestionDialog}
            loadInterviewQuestions={loadInterviewQuestions}
          />
        )}
      </Dialog>

      {/* Interview Question Table */}
      <DataTable
        value={filteredData}
        loading={loading}
        dataKey="id"
        paginator
        rows={10}
        header={
          <div className="flex justify-content-between align-items-center gap-3">
            <h3>Interview Question Management</h3>
            <div className="flex">
              <span className="p-input-icon-left mr-3">
                <i className="pi pi-search" />
                <input
                  type="text"
                  value={globalFilter}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  placeholder="Search by question, topic"
                  className="p-inputtext p-component"
                />
              </span>
              <Can
                I={PERMISSIONS_ACTION.INTERVIEW_QUESTION_ADD}
                a={PERMISSIONS_MODEL.INTERVIEW_QUESTION}>
                <Button label="New Question" onClick={openNew} />
              </Can>
            </div>
          </div>
        }>
        {questionColumns.map((col) => (
          <Column
            sortable
            key={col.field}
            field={col.field}
            header={col.header}
            body={col.body}
            style={col.style}
          />
        ))}

        <Column
          header="Actions"
          body={actionBodyTemplate}
          style={{ width: "10%" }}
        />
      </DataTable>

      {/* Delete Confirmation */}
      <ConfirmDialog
        visible={confirmDialog}
        onHide={() => setConfirmDialog(false)}
        message="Are you sure you want to delete this question?"
        header="Delete Confirmation"
        icon="pi pi-exclamation-triangle"
        accept={confirmDeleteQuestion}
        reject={() => setConfirmDialog(false)}
      />
    </>
  );
};

export default InterviewQuestionList;
